import type { FC } from "react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import TextareaAutosize from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ArrowRightIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";

interface FormInput {
  text: string;
}

export type ChatComposeProps = {
  className?: string;
  onSend?: (text: string) => void;
  placeholder?: string;
  /**
   * Optional key for form persistence.
   * If provided, the form state will be saved to localStorage under this key.
   * Like controlled/uncontrolled components, this should NEVER change during the component's lifetime.
   */
  persistKey?: string;
  /**
   * Context in which the chat compose is being used.
   * - "class": Used in class chat, requires canCreateChatMessage capability
   * - "ai": Used in AI chat, always allows sending messages
   */
  context?: "class" | "ai";
};

/*
 * Compose component for the chat.
 * Note that this is used for both the class chat and the AI chat,
 * which have different requirements.
 * Any user who has access to the AI chat should be able to send messages,
 * whereas we require a capabilities check for the class chat.
 */
export const ChatCompose: FC<ChatComposeProps> = ({
  className,
  onSend = () => {},
  placeholder,
  persistKey,
  context = "class",
}) => {
  const { canCreateChatMessage } = useCapabilities();

  // For AI chat, always allow sending messages. For class chat, check capability.
  const canSendMessage = context === "ai" || canCreateChatMessage;

  const formRef = useRef<HTMLFormElement>(null);
  const { register, handleSubmit, watch, setValue, reset } = useForm<FormInput>();

  let clearPersisted = () => {};
  // `persistKey` MUST be a stable string that does not change during the component's lifetime.
  if (persistKey) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const actions = useFormPersist(persistKey, {
      watch,
      setValue,
      storage: window.localStorage,
    });
    clearPersisted = actions.clear;
  }

  const onSubmit = handleSubmit(({ text }) => {
    onSend(text);
    clearPersisted(); // Clear the persisted form state (if any)
    reset(); // Reset the form after submission
  });

  const text = watch("text", "");

  return (
    <form
      className={twMerge(
        "flex w-full items-center justify-center rounded-lg border border-black-tint-70 p-1",
        className,
      )}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <TextareaAutosize
        {...register("text", { required: true, disabled: !canSendMessage })}
        className={twMerge("ml-2 grow resize-none", !canSendMessage && "italic")}
        maxRows={5}
        minRows={1}
        placeholder={canSendMessage ? placeholder : t("chat.join_class_to_chat")}
        wrap="soft"
        onKeyDown={(event) => {
          // When the user presses Enter, submit the form
          // unless they are holding the shift key, in which case allow new lines.
          if (event.key === "Enter" && !event.shiftKey) {
            formRef.current?.requestSubmit();
            event.preventDefault();
          }
        }}
      />

      <Button
        aria-label={t("chat.send_message")}
        className="bg-blue-tint-95 text-blue"
        disabled={text === ""}
        kind="squareLegacy"
        size="l"
        type="submit"
        isSquare
      >
        <ArrowRightIcon size="l" />
      </Button>
    </form>
  );
};
